/**
 * UNDO, AND THE DIFFERENCE BETWEEN A CHANGE AND A KEYSTROKE.
 *
 * A naive undo stack on a canvas is unusable in both directions. Push every React Flow change
 * and one drag of a node is ninety undo steps. Push only on a timer and Ctrl-Z loses the last
 * three things you did. So there are two verbs:
 *
 *   apply()   the graph changed but nothing was DECIDED — a node is mid-drag, a marquee is
 *             being drawn. Replaces the present, touches no history.
 *   commit()  a decision. Pushes the old present onto the past. A `coalesce` key merges runs
 *             of the same decision (typing into one field, dragging one node to rest) so one
 *             sentence typed is one undo, not forty.
 *
 * The stack is capped. Fifty graphs of a hundred nodes each is a few megabytes and an
 * operator who wants the fifty-first is asking for a version, not an undo.
 */
import { useCallback, useMemo, useRef, useState } from 'react';

import type { NdGraph } from './graph.ts';

const LIMIT = 50;
/** Two commits with the same key inside this window are one decision. */
const COALESCE_MS = 900;

export type History = {
  graph: NdGraph;
  canUndo: boolean;
  canRedo: boolean;
  /** Transient: replaces the present without a history entry. */
  apply: (next: NdGraph | ((current: NdGraph) => NdGraph)) => void;
  /** A decision: pushes the previous graph onto the undo stack. */
  commit: (next: NdGraph | ((current: NdGraph) => NdGraph), coalesceKey?: string) => void;
  undo: () => void;
  redo: () => void;
  /** After a load or a save: the new graph becomes the present and history starts over. */
  reset: (graph: NdGraph) => void;
  /** How many decisions deep we are — used to know whether anything is unsaved. */
  depth: number;
};

type Frame = { past: NdGraph[]; present: NdGraph; future: NdGraph[] };

export function useFlowHistory(initial: NdGraph): History {
  const [frame, setFrame] = useState<Frame>({ past: [], present: initial, future: [] });
  const lastCommit = useRef<{ key: string; at: number } | null>(null);

  const apply = useCallback((next: NdGraph | ((current: NdGraph) => NdGraph)) => {
    setFrame((f) => ({ ...f, present: typeof next === 'function' ? next(f.present) : next }));
  }, []);

  const commit = useCallback((next: NdGraph | ((current: NdGraph) => NdGraph), coalesceKey?: string) => {
    const now = Date.now();
    const previous = lastCommit.current;
    const merge =
      coalesceKey !== undefined &&
      previous !== null &&
      previous.key === coalesceKey &&
      now - previous.at < COALESCE_MS;
    lastCommit.current = coalesceKey ? { key: coalesceKey, at: now } : null;

    setFrame((f) => {
      const value = typeof next === 'function' ? next(f.present) : next;
      if (value === f.present) return f;
      if (merge) return { ...f, present: value, future: [] };
      const past = [...f.past, f.present];
      return {
        past: past.length > LIMIT ? past.slice(past.length - LIMIT) : past,
        present: value,
        future: [],
      };
    });
  }, []);

  const undo = useCallback(() => {
    lastCommit.current = null;
    setFrame((f) => {
      const previous = f.past[f.past.length - 1];
      if (!previous) return f;
      return { past: f.past.slice(0, -1), present: previous, future: [f.present, ...f.future] };
    });
  }, []);

  const redo = useCallback(() => {
    lastCommit.current = null;
    setFrame((f) => {
      const next = f.future[0];
      if (!next) return f;
      return { past: [...f.past, f.present], present: next, future: f.future.slice(1) };
    });
  }, []);

  const reset = useCallback((graph: NdGraph) => {
    lastCommit.current = null;
    setFrame({ past: [], present: graph, future: [] });
  }, []);

  return useMemo(
    () => ({
      graph: frame.present,
      canUndo: frame.past.length > 0,
      canRedo: frame.future.length > 0,
      apply,
      commit,
      undo,
      redo,
      reset,
      depth: frame.past.length,
    }),
    [frame, apply, commit, undo, redo, reset],
  );
}
